import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { filter, mapTo, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { DropdownDirection } from '../../../entities/dropdown-direction.entity';
import { FormInputWithDroppanelVariant } from '../../../entities/form-input-with-droppanel-variant.entity';
import { NgClass, AsyncPipe } from '@angular/common';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';

@Component({
    selector: 'sh-form-input-with-droppanel',
    templateUrl: './form-input-with-droppanel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShClickOutsideDirective, ShIconComponent, NgClass, AsyncPipe]
})
export class ShFormInputWithDroppanelComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly DELETE_INPUT_KEYS: string[] = ['Backspace', 'Delete', 'Clear'];

    public readonly iconSize: typeof IconSize = IconSize;
    public readonly isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input() id!: string;
    @Input() label!: string;
    @Input() disabled = false;
    @Input() variant: FormInputWithDroppanelVariant = FormInputWithDroppanelVariant.Default;
    @Input() openDirection: DropdownDirection = DropdownDirection.Below;
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;
    @Input() inOverflownContainer = false;

    @Output() deletePress: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    @ViewChild('droppanel') droppanel?: ElementRef<HTMLElement>;
    @ViewChild('droppanelToggle') droppanelToggle?: ElementRef<HTMLElement>;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.initAdjustDroppanelPositionSubscriber();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public toggleDropdown(): void {
        this.isOpen$.next(!this.isOpen$.value);
    }

    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    public handleDeletePress(keyboardEvent: KeyboardEvent): void {
        if (this.DELETE_INPUT_KEYS.includes(keyboardEvent.key)) {
            this.deletePress.emit(keyboardEvent);
        }
    }

    private initAdjustDroppanelPositionSubscriber(): void {
        if (!this.inOverflownContainer) {
            return;
        }

        // capture: true needed for capturing scroll event inside overflow containers
        const didScroll$: Observable<boolean> = fromEvent(window, 'scroll', { capture: true }).pipe(
            mapTo(true),
            startWith(true)
        );

        this.isOpen$
            .pipe(
                filter(isOpen => isOpen),
                switchMap(() => didScroll$),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.droppanel) {
                    const bottomY: number = this.droppanelToggle?.nativeElement.getBoundingClientRect().bottom || 0;
                    this.droppanel.nativeElement.style.top = `${bottomY}px`;
                }
            });
    }
}
